<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('office.title') }}
        </h6>

        <CreateButton
            :label="$t('office.create')"
            :to="{ name: 'officeForm' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('office.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :office-dropdown="officeDropdown"
            :federalStatesDropdown="federalStatesDropdown"
            :officeTypesDropdown="officeTypesDropdown"
            :officeEngagementTypesDropdown="officeEngagementTypesDropdown"
            @search="getOffices"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getOffices"/>

        <ExcelExportButton
            style="margin-right: 15px;"
            :label="$t('general.excel_export')"
            :loading="isExcelLoaded"
            @click="getExcelExport"/>

        <EmptyState
            v-if="!offices.data?.length"
            icon="fa-building"
            title="Keine Standorte gefunden"
            subtitle="Es wurden keine Standorte gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <a href="#" @click="sortBy('name')">
                  <span>{{ $t('general.office_name') }}</span>
                  <i :class="getIconClass('name')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('address')">
                  <span>{{ $t('general.street') }}</span>
                  <i :class="getIconClass('address')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('zip')" style="min-width: 200px;">
                  <span>{{ $t('general.postal_code') }}</span>
                  <i :class="getIconClass('zip')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('city')" style="min-width: 200px;">
                  {{ $t('general.city') }}
                  <i :class="getIconClass('city')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('engagementType')">
                  {{ $t('general.office_engagement_type') }}
                  <i :class="getIconClass('engagementType')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('federalState')">
                  {{ $t('general.federal_state') }}
                  <i :class="getIconClass('federalState')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('region')">
                  {{ $t('general.region') }}
                  <i :class="getIconClass('region')"></i>
                </a>
              </th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(office) in offices.data" :key="office.id">
              <td>{{ office?.name }}</td>
              <td>{{ office?.address }}</td>
              <td>{{ office?.zip }}</td>
              <td>{{ office?.city }}</td>
              <td>{{ office?.engagementTypeTranslation }}</td>
              <td>{{ office?.federalStateTranslation }}</td>
              <td>{{ office?.regionTranslation }}</td>
              <td class="text-right">
                <button class="btn btn-primary"
                        @click="router.push({name: 'officeForm', params: {
                          id: office?.id
                        } })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ml-2"
                        @click="deleteModal(office.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="offices?.pagination?.total_pages > 1"
            :totalPages="offices?.pagination?.total_pages"
            :currentPage="offices?.pagination?.current_page"
            :totalItems="offices?.pagination?.total_items"
            :perPage="offices?.pagination?.items_per_page"
            @pagechanged="getOffices"/>
      </div>
    </div>

    <ModalDelete
        @update:modelValue="updateModal"
        :modelValue="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="deleteOffice">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup lang="ts">
import officeService from '@/api/Office';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
// @ts-ignore
import {getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Search from '@/views/App/Office/Search.vue';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {IOfficeFilters, IOfficeList, IPagination} from "@/interfaces/Office/IOffice";
import {ICoachingOverviewFilters} from "@/interfaces/CoachingOverview/ICoachingOverviewFIlters";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const {t} = useI18n();
const isExcelLoaded = ref(false);

const offices = ref<IOfficeList>({
  data: [],
  pagination: {
    total_items: 0,
    total_pages: 0,
    current_page: 1,
    items_per_page: 10,
  } as IPagination,
});

const filters = ref<IOfficeFilters>({
  officeId: '',
  engagementType: '',
  type: '',
  federalState: '',
  orderBy: 'DESC',
  sortBy: '',
  page: 1,
  limit: 10,
});

const showModal = ref(false);
const officeId = ref(0);
const isSearching = ref(false);
const isLoaded = ref(false);
const officeDropdown = ref([]);
const officeTypesDropdown = ref([]);
const federalStatesDropdown = ref([]);
const officeEngagementTypesDropdown = ref([]);

async function getOffices(
    page = 1,
    perPage?: number,
    passedFilters?: ICoachingOverviewFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = offices.value?.pagination?.items_per_page;

  try {
    const {data} = await officeService.getOffices({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes,
    });

    offices.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function deleteOffice() {
  try {
    await officeService.deleteOffice(officeId.value);

    filters.value.officeId = '';
    filters.value.engagementType = '';
    filters.value.type = '';
    filters.value.federalState = '';
    filters.value.orderBy = 'DESC'; // Keep this as 'DESC' if you don't want to change it
    filters.value.sortBy = '';
    showModal.value = false;

    await getOffices();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key: string) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy:
        filters.value.orderBy === 'DESC' ||
        filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getOffices();
}

function updateModal(value: boolean) {
  CloseToast();
  showModal.value = value;
}

function deleteModal(id: number) {
  CloseToast();
  officeId.value = id;
  showModal.value = true;
}

async function getOfficeEnumDropdowns() {
  federalStatesDropdown.value = getEnums.value?.federalStates || [];
  officeEngagementTypesDropdown.value = getEnums.value?.officeEngagementTypes || [];
  officeTypesDropdown.value = getEnums.value?.officeTypes || [];
}


async function getExcelExport() {
  await downloadFile('/office/excel-export', undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getOfficeEnumDropdowns(),
    getOfficeDropdown(officeDropdown, t),
    getOffices(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>

</style>